import type { ReactNode } from 'react';
import { startTransition, useCallback, useMemo, useState } from 'react';
import { hydrateRoot } from 'react-dom/client';

import createEmotionCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { RemixBrowser } from '@remix-run/react';
import { RemixApptoolboxInject } from 'platform/components/RemixApptoolboxInject';
import { ODTeamBoundary } from 'platform/ODTeamBoundary';
import { ClientOnly } from 'platform/remix-run/utils/client-only';
import { ClientStyleContext } from 'platform/styles/client.context';
import 'preloadQueries';
import { RAILS_ENV } from 'shared/helpers/globals';

function ClientCacheProvider({ children }: { children: ReactNode }) {
  const [cache, setCache] = useState(createEmotionCache({ key: 'css' }));

  const reset = useCallback(() => {
    return setCache(createEmotionCache({ key: 'css' }));
  }, []);

  const context = useMemo(() => ({ reset }), [reset]);

  return (
    <ClientStyleContext.Provider value={context}>
      <CacheProvider value={cache}>{children}</CacheProvider>
    </ClientStyleContext.Provider>
  );
}

const hydrate = () => {
  startTransition(() => {
    hydrateRoot(
      document,
      <ODTeamBoundary team="seller">
        <ClientCacheProvider>
          <ClientOnly>
            {() =>
              // Do not inject the Apptoolbox widget in test environments
              RAILS_ENV !== 'test' && <RemixApptoolboxInject />
            }
          </ClientOnly>
          <RemixBrowser />
        </ClientCacheProvider>
      </ODTeamBoundary>,
    );
  });
};

if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  setTimeout(hydrate, 1);
}
